/* eslint-disable react-hooks/exhaustive-deps */
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Row, Col, Button, Form, Space, message, Typography, Input, Image } from "antd";
import { AreaChartOutlined, CheckCircleOutlined, FileSyncOutlined, LoginOutlined, PlusCircleOutlined, SettingOutlined } from "@ant-design/icons";
import { DOMAIN_ID, ORG_ID } from '../../../constants';
import { FormInput, FormSelect } from "../../../components/ui/UserInteraction";
import { logOutUser } from '../../../common';
import { MoveRight } from 'lucide-react';
import { Box } from '@mui/material';
import { dispatchAdDomain } from '../../../utils/apiservice';
import step1Img from '../../../assets/entra-config-img-step/entra-step-1.png'
import step2Img from '../../../assets/entra-config-img-step/entra-step-2.png'
import step3Img from '../../../assets/entra-config-img-step/entra-step-3.png'
import step4Img from '../../../assets/entra-config-img-step/entra-step-4.png'
import step5Img from '../../../assets/entra-config-img-step/entra-step-5.png'
import step6Img from '../../../assets/entra-config-img-step/entra-step-6.png'
import { Download, DownloadOutlined } from '@mui/icons-material';
import { isEmpty } from '../../../utils/helpers';

export default function IdentityEntra() {
  const navigate = useNavigate();
  const [notification, setNotification] = message.useMessage();

  // loaders
  const [isLoading, setIsLoading] = useState(false);

  const [entraForm] = Form.useForm();
  const [entraFormData, setEntraFormData] = useState();

  const { Text, Title } = Typography;


  /// methods
  const handleEntraConfigure = async () => {
    setIsLoading(true);
    const isValid = entraForm.isFieldValidating();

    if (!isValid) {
      const values = entraForm.getFieldsValue();
      const payload = {
        "orgId": ORG_ID(),
        "tenantId": DOMAIN_ID(),
        "directoryName": values?.entraName,
        "identitySource": "EntraID",
        "integrationType": values?.integrationType,
        "entra_tenant_id": values?.tenant_id,
        "client_id": values?.client_id,
        // The form field holds the real value. It used to be rebuilt one
        // keystroke at a time, which lost pasted and autofilled secrets.
        "client_secret": String(values?.client_Secret || "").trim()
      }
      dispatchAdDomain({ payload, onSuccess: handleAdDomainSuccess, onFailure: handleApiFailure, onError: handleApiError })
    }
  }

  const handleAdDomainSuccess = (res) => {
    setIsLoading(false);
    notification.success({ content: "Integrate EntraID SuccessFully." });
    setTimeout(() => {
      navigate('/pam/directory', { state: { filter: { directoryName: entraForm.getFieldValue("entraName") } } })
    }, 500)
  }

  //// common for all api calls
  const handleApiFailure = ({ message, isLogout = false }) => {
    setIsLoading(false);
    notification.error({ content: message });
    if (isLogout) logOutUser();
  }

  const handleApiError = ({ message }) => {
    setIsLoading(false);
    notification.error({ content: message });
  }


  const EntraDirectoryAgent = () => {

    const buildEnvContent = () => {
      return `ORG_ID=${ORG_ID()}
TENANT_ID=${DOMAIN_ID()}
`;
    };

    const downloadEnvFile = async () => {
      const content = buildEnvContent();
      const blob = new Blob([content], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "app.env";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      notification.success({ content: "app.env downloaded successfully." });
    };

    const EntraConfigureForm = () => {
      return (<div className='p-3'>
        <Row className="justify-between">
          <Col span={14}>
            <FormInput
              name="entraName"
              label="Entra Name"
              rules={[
                {
                  required: true,
                  message: "Entra Name is required",
                },
              ]}
              placeholder="Entra Name"
            />
          </Col>
          <Col span={9} id="integrationType">
            <FormSelect
              name="integrationType"
              label={"Select Integration Type"}
              rules={[
                {
                  required: true,
                  message: "Select Integration Type",
                },
              ]}
              placeholder="Select Integration Type"
              options={[
                { value: "Agentless", label: <span>Agentless</span> },
                { value: "AgentBased", label: <span>AgentBased</span> }
              ]}
            />
          </Col>
          <Col span={24}>
            <FormInput
              name="tenant_id"
              label="Tenant ID"
              rules={[
                {
                  required: true,
                  message: "Tenant ID is required",
                },
              ]}
              placeholder="Tenant ID"
            />
          </Col>
          <Col span={24}>
            <FormInput
              name="client_id"
              label="Client ID"
              rules={[
                {
                  required: true,
                  message: "Client ID is required",
                },
              ]}
              placeholder="Client ID"
            />
          </Col>
          <Col span={24}>
            <Form.Item name="client_Secret" label="Client Secret" rules={[
              {
                required: true,
                message: "Client Secret is required",
              },
            ]}>
              <Input.Password autoComplete="new-password" placeholder="Client secret value" />
            </Form.Item>
            <div style={{ fontSize: 12, color: "var(--an-muted)", marginTop: -16, marginBottom: 12, lineHeight: 1.5 }}>
              Paste the secret's <strong>Value</strong>, not its Secret ID. Azure shows the Value only once, right
              after you create the secret.
            </div>
          </Col>
        </Row>
      </div>)
    }

    const steps = [
      {
        title: "Access Entra Admin Center",
        icon: LoginOutlined,
        isImage: true,
        link: step1Img,
        description: [
          { isHeader: true, value: "Sign in to Microsoft Entra Admin Center" },
          { isLink: "https://entra.microsoft.com", value: "https://entra.microsoft.com" },
          { value: "Log in using an account with admin permissions" }
        ]
      },
      {
        title: "Register a New Application",
        icon: PlusCircleOutlined,
        isImage: true,
        link: step2Img,
        description: [
          { isHeader: true, value: "Create your application registration" },
          { value: <>In the left menu, go to <b>"Applications"</b> → <b>"App registrations"</b></> },
          { value: <>Click <b>"+ New registration"</b></> },
          { value: "Fill out the registration form with these details:" },
          { isInfo: true, value: `Name: [Your application name] Supported account types: "Accounts in this organizational directory only" Redirect URI: Leave blank for now` },
          { value: "Navigate to your localhost URL or backend callback URL" },
          { value: <>Click <b>"Register"</b></> }
        ]
      },
      {
        title: "Retrieve Client & Tenant IDs",
        icon: FileSyncOutlined,
        isImage: true,
        link: step3Img,
        description: [
          { isHeader: true, value: "Copy essential identifiers" },
          { value: `After registration, you'll be taken to the app overview page` },
          { value: <><b>Client ID:</b> Copy the Application (client) ID</> },
          { value: <><b>Tenant ID:</b> Copy the Directory (tenant) ID</> },
          { value: "Store these values securely - you'll need them for configuration" }
        ]
      },
      {
        title: "Generate Client Secret",
        icon: PlusCircleOutlined,
        isImage: true,
        link: step4Img,
        description: [
          { isHeader: true, value: "Create authentication credentials" },
          { value: <>In the left sidebar, go to <b>"Client secrets"</b></> },
          { value: <>Under Client secrets, click <b>"+ New client secret"</b></> },
          { value: `Add a description (e.g., "AppSecret")` },
          { value: `Choose expiration period (recommended: 6 months or 1 year)` },
          { value: <>Click <b>"Add"</b></> },
          { isWarning: true, value: <><b>Critical:</b> Copy the secret value immediately and store it somewhere safe. You cannot view it again later!</> }
        ]
      },
      {
        title: "Configure API Permissions",
        icon: AreaChartOutlined,
        isImage: true,
        link: step5Img,
        description: [
          { isHeader: true, value: "Create authentication credentials" },
          { value: <>Go to <b>"API permissions"</b> in the left sidebar</> },
          { value: <>Click <b>"+ Add a permission"</b></> },
          { value: <>Select <b>"Microsoft Graph"</b></> },
          { value: <>Choose <b>"Application permissions" (not delegated)</b></> },
          { isHeader: true, value: "Required permissions to add:" },
          { isInfo: true, value: `• User.Read.All • Group.Read.All • Directory.Read.All (Optional - if syncing more details)` },
          { value: `After adding permissions, click "Grant admin consent for [Your Org]"` }
        ]
      },
      {
        title: "Verify Configuration",
        icon: CheckCircleOutlined,
        isImage: true,
        link: step6Img,
        description: [
          { isHeader: true, value: "Final verification checklist" },
          { value: <>Ensure your app shows <b>"Granted"</b> status for all permissions</> },
          { value: `Verify you have saved the following securely:` },
          { isInfo: true, value: <div className='space-x-2'><span>✓ Client ID (Application ID)</span> <span>✓ Tenant ID (Directory ID)</span><span>✓ Client Secret (Value)</span></div> },
        ]
      },
      {
        title: "Configure Entra Credentials",
        icon: SettingOutlined,
        isForm: true,
        link: <EntraConfigureForm />,
        description: [
          { isHeader: true, value: "Enter credentials in your application" },
          { value: <>Enter the <b>Entra Name</b> in the designated field</> },
          { value: <>Select the <b>Integration type</b> in the designated field</> },
          { value: <>Enter the <b>Tenant ID</b> in the designated field</> },
          { value: <>Enter the <b>Client ID</b> in the designated field</> },
          { value: <>Enter the <b>Client Secret</b> in the designated field</> },
        ]
      },
      // {
      //   title: "Complete Integration Setup",
      //   icon: <Download />,
      //   link: null,
      //   description: [
      //     { isHeader: true, value: "Follow the step to complete the setup" },
      //     { value: <>Download the script</> },
      //     {
      //       isInfo: true, value:
      //         <div className='space-y-2 flex flex-col'>
      //           <span className='text-sm'>Run the following command to download the installation script:</span>
      //           <Input
      //             className='border-none'
      //             readOnly
      //             value="wget -O entra-script.ps1 https://raw.githubusercontent.com/authnull0/windows-endpoint/main/entra-script.ps1"
      //             suffix={
      //               <Text
      //                 copyable={{
      //                   text: "wget -O entra-script.ps1 https://raw.githubusercontent.com/authnull0/windows-endpoint/main/entra-script.ps1",
      //                   tooltips: ["Copy Command", "Copied"],
      //                 }}
      //                 style={{ color: "GrayText" }}
      //               />
      //             }
      //           />

      //         </div>
      //     },
      //     { value: <>Download app.env</> },
      //     {
      //       isAction: true, value: <Button type="primary" onClick={() => downloadEnvFile()} icon={<DownloadOutlined />}>
      //         Download app.env
      //       </Button>
      //     },
      //     { value: <>Copy them in your windows vm in <b>C:\Users\username\Desktop</b></> },
      //     { value: <>Run the script</> },
      //     {
      //       isInfo: true, value:
      //         <div className='space-y-2 flex flex-col'>
      //           <span className='text-sm'>Install the agent using:</span>
      //           <Input
      //             className='border-none'
      //             readOnly
      //             value="./entra-script.ps1"
      //             suffix={
      //               <Text
      //                 copyable={{
      //                   text: "./entra-script.ps1",
      //                   tooltips: ["Copy Command", "Copied"],
      //                 }}
      //                 style={{ color: "GrayText" }}
      //               />
      //             }
      //           />

      //         </div>
      //     },
      //     { value: <>Click <b>"Configure EntraID"</b> to complete the setup</> }
      //   ]
      // },
    ]

    const InfoStructure = ({ data, stepNumber }) => {
      // const Icon = data?.icon;
      const isSingleCol = isEmpty(data?.link)
      const isForm = data?.isForm || false;
      return (
        <Box className={`!rounded-lg !shadow-md p-3 border-t-4 border-[#001529] md:min-h-[300px]`}>
          <Box className="space-x-3 flex items-center">
            <div className='p-2 bg-sky-700 rounded-full !size-10 flex items-center justify-center shadow-lg drop-shadow'>
              <Title className='!mb-0.5 !text-white !leading-none !text-shadow-lg/20' level={3} >{stepNumber}</Title>
              {/* <Icon className='text-white text-lg' /> */}
            </div>
            <Title className='!mb-0' level={4}>{data?.title}</Title>
          </Box>
          <Box className={`h-full w-full grid md:grid-cols-${isForm ? "5" : (isSingleCol ? "1" : "2")}`}>
            <Box className={`${isForm ? "col-span-2" : ""}`}>
              <ul className='list-none ps-1 space-y-3 mt-3'>
                {
                  data?.description?.map((item) => {
                    if (item?.isHeader) return (<li><Title className='!mb-0' level={5}>{item?.value}</Title></li>)
                    if (item?.isLink) return (<li className='space-x-2.5 flex items-center'>
                      <MoveRight className="stroke-[0.8] stroke-sky-600" />
                      <div>
                        <Text>Navigate to </Text><Link to={item?.isLink} >{`${item?.value}`}</Link>
                      </div>
                    </li>)
                    if (item?.isInfo) return (
                      <li className='ms-4'>
                        <div className='bg-black/60 rounded-md p-2 '>
                          <Text className='text-white'>{item?.value}</Text>
                        </div>
                      </li>
                    )
                    if (item?.isWarning) return (
                      <li className='ms-4'>
                        <div className='bg-yellow-500 rounded-md p-2 '>
                          <Text>{item?.value}</Text>
                        </div>
                      </li>
                    )

                    if (item?.isAction) return (<li className='ms-4'>{item?.value}</li>)

                    return (<li className='space-x-2.5 flex items-center'><MoveRight className="stroke-[0.8] stroke-sky-600" /><Text>{item?.value}</Text></li>)
                  })
                }
              </ul>
            </Box>
            <Box className={`h-full p-3 ${isForm ? "col-span-3" : ""}`}>
              <Box className="border-2 h-fit border-dashed rounded">
                {data?.isImage && <Image className={`${stepNumber === 1 ? "md:max-h-[300px]" : "!h-full"}`} src={data?.link} alt={data?.link} />}
                {data?.isForm && data?.link}
              </Box>
            </Box>
          </Box>
        </Box>
      )
    }

    return (
      <>
        <Space className="!w-full" direction="vertical">
          <Form
            form={entraForm}
            layout="vertical"
            fields={entraFormData}
            onFinish={handleEntraConfigure}
          >
            <Row>
              <Col className='space-y-4' span={24}>
                {
                  steps?.map((item, index) => {
                    return <InfoStructure data={item} stepNumber={index + 1} />
                  })
                }
              </Col>
            </Row>
            <Row style={{ marginTop: "1rem" }} justify="space-between">
              <Col></Col>
              <Col>
                <Button
                  style={{ marginRight: "1rem" }}
                  onClick={() => navigate(-1)}
                >
                  Back
                </Button>
                <Button id="nextBtn" type="primary" htmlType="submit" loading={isLoading}>
                  Configure EntraID
                </Button>
              </Col>
            </Row>
          </Form>
        </Space >
      </>
    )
  }

  // const steps = [
  //   {
  //     title: "Configure EntraID",
  //     content: <EntraDirectoryAgent />
  //   }
  // ]

  // const items = steps.map((item) => ({
  //   key: item.title,
  //   title: item.title,
  // }));

  return (
    <>
      <Row className="content-conatiner">
        {setNotification}
        <Col span={20} className="space-y-6">
          <Row justify="space-between">
            <Col>
              <h2 className="title !mb-3">{`Integrate EntraID`}</h2>
            </Col>
          </Row>
          <EntraDirectoryAgent />
          {/* <Row>
            <Col span={24}>
              <Steps current={current} items={items} />
            </Col>
          </Row>
          <Row>
            <Col span={24} id="entraConfiguration">
              {steps[current]?.content}
            </Col>
          </Row> */}
        </Col>
      </Row>
    </>
  )
}